'use client';

import { use, useSyncExternalStore } from 'react';
import { FormProvider } from 'react-hook-form';

import styled from '@emotion/styled';

import type { ResponderPanel } from '@/shared-survey';
import {
  SurveyThemeContext,
  type SurveyThemeOrgSettings,
} from '@/shared-utilities';
import { type Channel } from '@/survey-graphql';

import SurveyAiFormsDesktopLayout from './survey-ai-forms-desktop-layout';
import { useSurveyAiFormsFormProvider } from './survey-ai-forms-form.hooks';
import SurveyAiFormsMobileLayout from './survey-ai-forms-mobile-layout';
import { useSurveyAiForms } from './survey-ai-forms.hooks';
import type { SurveyAiFormsProjectInput } from './survey-ai-forms.types';

interface Props {
  /** Project data shape (sub-set of GetProjectQuery['project']) — domain-neutral */
  project: SurveyAiFormsProjectInput;
  /** Organization settings including logo and branding (survey or manager schema) */
  orgSettings?: SurveyThemeOrgSettings | null;
  /** Session ID for form submission tracking */
  sessionId: string;
  /** Whether this is preview mode (for manager app) */
  isPreviewMode?: boolean;
  channel?: Channel;
  panel?: ResponderPanel | null;
}

function emptySubscribe() {
  return () => {
    // useSyncExternalStore requires a subscribe function; no external store to subscribe to
  };
}

function useIsHydrated() {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  );
}

const ThemedContainer = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

export default function SurveyAiFormsContent({
  project,
  sessionId,
  isPreviewMode = false,
  channel,
  panel = null,
}: Props) {
  const { styles } = use(SurveyThemeContext);
  const isHydrated = useIsHydrated();

  // Initialize React Hook Form
  const form = useSurveyAiFormsFormProvider({
    questions: project.questions ?? [],
  });

  const {
    displayProject,
    questions,
    isMobile,
    viewMode,
    responseMode,
    currentQuestion,
    currentQuestionId,
    currentQuestionIndex,
    showInstructions,
    progress,
    estimatedTotalTimeInSeconds,
    handleAiResponseSubmit,
    handleNext,
    handleFormResponseSubmit,
    handleQuestionFocus,
    handleQuestionNavigate,
    handleViewModeToggle,
    handleSubmit,
    handleFormSubmit,
    handleCloseInstructions,
    registerVoiceStopCallback,
    saveTranscript,
    getTranscript,
    canSubmit,
    isAddingMessage,
    isSubmitting,
    isProcessingResponse,
    submittedQuestionIds,
    validationErrors,
    hasTriedSubmit,
    isAIFormAssistanceEnabled,
  } = useSurveyAiForms({
    project,
    form,
    sessionId,
    isPreviewMode,
    channel,
    panel,
  });

  return (
    <FormProvider {...form}>
      <ThemedContainer
        data-test='survey-ai-forms-root'
        data-hydrated={isHydrated}
        themeStyles={styles}
        className='data-[hydrated=false]:opacity-0'
      >
        {/* Render mobile layout for mobile devices */}
        {isMobile ? (
          <SurveyAiFormsMobileLayout
            project={displayProject}
            currentQuestion={currentQuestion}
            currentQuestionId={currentQuestionId}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            responseMode={responseMode}
            viewMode={viewMode}
            progress={progress}
            estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
            onResponseSubmit={handleAiResponseSubmit}
            onQuestionFocus={handleQuestionFocus}
            onQuestionNavigate={handleQuestionNavigate}
            saveTranscript={saveTranscript}
            getTranscript={getTranscript}
            onViewModeToggle={handleViewModeToggle}
            onSubmit={handleSubmit}
            onFormSubmit={handleFormSubmit}
            registerVoiceStopCallback={registerVoiceStopCallback}
            isProcessingResponse={isProcessingResponse}
            isSubmitting={isSubmitting}
            submittedQuestionIds={submittedQuestionIds}
            canSubmit={canSubmit}
            validationErrors={validationErrors}
            hasTriedSubmit={hasTriedSubmit}
            isPreviewMode={isPreviewMode}
            isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
          />
        ) : (
          <SurveyAiFormsDesktopLayout
            project={displayProject}
            currentQuestion={currentQuestion}
            currentQuestionId={currentQuestionId}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            responseMode={responseMode}
            viewMode={viewMode}
            progress={progress}
            estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
            onResponseSubmit={handleAiResponseSubmit}
            onNext={handleNext}
            onQuestionFocus={handleQuestionFocus}
            onQuestionNavigate={handleQuestionNavigate}
            saveTranscript={saveTranscript}
            getTranscript={getTranscript}
            onViewModeToggle={handleViewModeToggle}
            onSubmit={handleSubmit}
            onFormSubmit={handleFormSubmit}
            registerVoiceStopCallback={registerVoiceStopCallback}
            onCloseInstructions={handleCloseInstructions}
            onFormResponseSubmit={handleFormResponseSubmit}
            showInstructions={showInstructions}
            canSubmit={canSubmit}
            isAddingMessage={isAddingMessage}
            isSubmitting={isSubmitting}
            isProcessingResponse={isProcessingResponse}
            submittedQuestionIds={submittedQuestionIds}
            validationErrors={validationErrors}
            hasTriedSubmit={hasTriedSubmit}
            isPreviewMode={isPreviewMode}
            isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
          />
        )}
      </ThemedContainer>
    </FormProvider>
  );
}
